import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel, useScrollTo, preselectBuilding, OPS_OPTION } from "./Reveal";

const AUDIENCES = [
  {
    title: "STARTING A RESTAURANT",
    copy: "Turn your food idea into a practical kitchen and operating model.",
    img: "/assets/img/skope-kitchen-1.jpg",
    alt: "Commercial kitchen cooking line at Skope Kitchens",
    span: "lg:col-span-3",
  },
  {
    title: "LAUNCHING A FOOD BRAND",
    copy: "Build the operational foundation before investing heavily in infrastructure.",
    img: "/assets/img/food-premium.jpg",
    alt: "Premium plated dish being finished by a chef",
    span: "lg:col-span-3",
  },
  {
    title: "SCALING AN EXISTING BRAND",
    copy: "Create kitchen systems that can be replicated across locations.",
    img: "/assets/img/skope-kitchen-3.jpg",
    alt: "End-to-end kitchen operations in a commercial kitchen",
    span: "lg:col-span-2",
  },
  {
    title: "OPTIMISING AN EXISTING KITCHEN",
    copy: "Find bottlenecks in manpower, workflow, capacity and kitchen design.",
    img: "/assets/img/skope-kitchen-2.jpg",
    alt: "Cold and dry storage inside a commercial kitchen",
    span: "lg:col-span-2",
  },
  {
    title: "RETHINKING THE MENU",
    copy: "Understand how menu complexity affects production and operations.",
    img: "/assets/img/chef-plating.jpg",
    alt: "Chef carefully plating a dish in a professional kitchen",
    span: "lg:col-span-2",
  },
];

const WhoWeHelp = () => {
  const scrollTo = useScrollTo();
  return (
    <section id="who-we-help" className="relative bg-[#f6f6f6] py-28 lg:py-40 border-y border-ink/5" data-testid="who-we-help-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <Reveal><SectionLabel>Who We Help — 02</SectionLabel></Reveal>
        <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal delay={0.1}>
            <h2 className="font-display font-semibold text-3xl lg:text-5xl tracking-tight text-ink max-w-2xl leading-[1.08]" data-testid="who-we-help-headline">
              BUILT FOR PEOPLE BUILDING FOOD BUSINESSES.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-ink/45 max-w-sm text-sm leading-relaxed">
              First restaurant or tenth outlet — the question is the same:
              is the kitchen built for the business you want to become?
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-6 gap-px bg-ink/10 border border-ink/10">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.title} delay={0.06 * i} className={a.span}>
              <button
                onClick={() => scrollTo("#contact")}
                className="group relative w-full h-full min-h-[300px] text-left bg-white p-8 flex flex-col justify-between overflow-hidden"
                data-testid={`audience-card-${i + 1}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-0 group-hover:opacity-25 transition-opacity duration-700"
                  style={{ backgroundImage: `url(${a.img})` }}
                  role="img"
                  aria-label={a.alt}
                />
                <div className="relative flex items-start justify-between">
                  <span className="font-mono text-xs text-skope/80">0{i + 1}</span>
                  <ArrowUpRight size={18} className="text-ink/25 group-hover:text-skope group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500" />
                </div>
                <div className="relative">
                  <h3 className="font-display font-semibold text-xl lg:text-2xl tracking-tight text-ink group-hover:text-skope transition-colors duration-500">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm text-ink/50 leading-relaxed max-w-xs">{a.copy}</p>
                </div>
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-skope group-hover:w-full transition-all duration-700" />
              </button>
            </Reveal>
          ))}
          <Reveal delay={0.36} className="lg:col-span-6">
            <button
              onClick={() => { preselectBuilding(OPS_OPTION); scrollTo("#contact"); }}
              className="group relative w-full text-left bg-skope/[0.06] border border-skope/30 p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-6 overflow-hidden hover:border-skope/60 transition-colors duration-500"
              data-testid="audience-card-handover"
            >
              <div className="flex-1">
                <span className="font-mono text-xs text-skope">06</span>
                <h3 className="mt-2 font-display font-semibold text-xl lg:text-2xl tracking-tight text-ink">
                  UNABLE TO RUN YOUR CLOUD KITCHEN OR RESTAURANT PROPERLY?
                </h3>
                <p className="mt-2 text-sm text-ink/55 leading-relaxed max-w-xl">
                  Hand your complete kitchen and aggregator operations to us — end to end —
                  and get back to building your brand.
                </p>
              </div>
              <span className="flex items-center gap-3 font-mono text-[11px] tracking-[0.2em] uppercase text-skope shrink-0">
                Hand Over Operations
                <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500" />
              </span>
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default WhoWeHelp;
